import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Star, TrendingUp } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
interface StoreDetailPanelProps {
  isOpen: boolean;
  onClose: () => void;
  store: any;
}
const data = [{
  name: 'Jan',
  score: 4.1
}, {
  name: 'Feb',
  score: 4.2
}, {
  name: 'Mär',
  score: 4.0
}, {
  name: 'Apr',
  score: 4.3
}, {
  name: 'Mai',
  score: 4.5
}, {
  name: 'Jun',
  score: 4.6
}];
export function StoreDetailPanel({
  isOpen,
  onClose,
  store
}: StoreDetailPanelProps) {
  return <AnimatePresence data-id="element-82">
      {isOpen && <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} className="absolute right-0 top-0 bottom-0 w-[400px] bg-white shadow-2xl z-[500] border-l border-gray-200 flex flex-col" data-id="element-83">
          {/* Header */}
          <div className="relative h-48 bg-gray-900" data-id="element-84">
            <img src="https://images.unsplash.com/photo-1555215695-3004980ad54e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="BMW Autohaus Außenansicht" className="w-full h-full object-cover opacity-80" data-id="element-85" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-6" data-id="element-86">
              {store?.type && <Badge variant="warning" className="self-start mb-2" data-id="element-87">
                  {store.type}
                </Badge>}
              <h2 className="text-2xl font-bold text-white leading-tight" data-id="element-88">
                {store?.name || 'BMW Niederlassung'}
              </h2>
              <p className="text-white/80 text-sm flex items-center gap-1" data-id="element-89">
                <MapPinIcon className="w-3 h-3" data-id="element-90" />{' '}
                {store?.city ? `${store.city}, ${store.country}` : store?.address || ''}
              </p>
            </div>
            <div className="absolute top-4 left-4" data-id="element-91">
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/4/44/BMW.svg/250px-BMW.svg.png" alt="BMW" className="h-8 w-auto opacity-90" data-id="element-92" />
            </div>
            <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-black/20 hover:bg-black/40 text-white rounded-full transition-colors backdrop-blur-sm" data-id="element-93">
              <X size={20} data-id="element-94" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-95">
            {/* Contact Info */}
            <div className="space-y-2" data-id="element-96">
              <h3 className="text-sm font-semibold text-gray-900" data-id="element-97">Kontakt</h3>
              <div className="bg-gray-50 rounded-lg border border-gray-100 divide-y divide-gray-100" data-id="element-98">
                <div className="flex items-center gap-3 px-4 py-2.5 text-sm" data-id="element-99">
                  <MapPinIcon className="w-4 h-4 text-gray-400 shrink-0" data-id="element-100" />
                  <span className="text-gray-700" data-id="element-101">{store?.address || '–'}</span>
                </div>
                {store?.phone && <div className="flex items-center gap-3 px-4 py-2.5 text-sm" data-id="element-102">
                    <PhoneIcon className="w-4 h-4 text-gray-400 shrink-0" data-id="element-103" />
                    <a href={`tel:${store.phone}`} className="text-blue-600 hover:underline" data-id="element-104">
                      {store.phone}
                    </a>
                  </div>}
                {store?.email && <div className="flex items-center gap-3 px-4 py-2.5 text-sm" data-id="element-105">
                    <MailIcon className="w-4 h-4 text-gray-400 shrink-0" data-id="element-106" />
                    <a href={`mailto:${store.email}`} className="text-blue-600 hover:underline truncate" data-id="element-107">
                      {store.email}
                    </a>
                  </div>}
                {store?.website && <div className="flex items-center gap-3 px-4 py-2.5 text-sm" data-id="element-108">
                    <GlobeIcon className="w-4 h-4 text-gray-400 shrink-0" data-id="element-109" />
                    <a href={store.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate" data-id="element-110">
                      {store.website.replace(/^https?:\/\//, '')}
                    </a>
                  </div>}
              </div>
            </div>

            {/* Key Stats – only shown when score is available */}
            {store?.score !== undefined && <div className="grid grid-cols-2 gap-4" data-id="element-111">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-112">
                  <div className="text-sm text-gray-500 mb-1" data-id="element-113">
                    Google Bewertung
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-114">
                    <span className="text-3xl font-bold text-gray-900" data-id="element-115">
                      {store.score}
                    </span>
                    <div className="flex text-yellow-400" data-id="element-116">
                      <Star size={14} fill="currentColor" data-id="element-117" />
                      <Star size={14} fill="currentColor" data-id="element-118" />
                      <Star size={14} fill="currentColor" data-id="element-119" />
                      <Star size={14} fill="currentColor" data-id="element-120" />
                      <Star size={14} fill="currentColor" className="text-gray-300" data-id="element-121" />
                    </div>
                  </div>
                  <div className="text-xs text-green-600 font-medium mt-1 flex items-center" data-id="element-122">
                    <TrendingUp size={12} className="mr-1" data-id="element-123" /> +0.2 vs. Vormonat
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-124">
                  <div className="text-sm text-gray-500 mb-1" data-id="element-125">
                    Bewertungen gesamt
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-126">
                    <span className="text-3xl font-bold text-gray-900" data-id="element-127">
                      1.248
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1" data-id="element-128">
                    12 neue diese Woche
                  </div>
                </div>
              </div>}

            {/* Trend Chart */}
            <div data-id="element-129">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center justify-between" data-id="element-130">
                Bewertungstrend (6 Monate)
                <span className="text-xs font-normal text-gray-500" data-id="element-131">
                  Live-Daten
                </span>
              </h3>
              <div className="h-40 w-full" data-id="element-132">
                <ResponsiveContainer width="100%" height="100%" data-id="element-133">
                  <AreaChart data={data} data-id="element-134">
                    <defs data-id="element-135">
                      <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1" data-id="element-136">
                        <stop offset="5%" stopColor="#1C69D4" stopOpacity={0.15} data-id="element-137" />
                        <stop offset="95%" stopColor="#1C69D4" stopOpacity={0} data-id="element-138" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" data-id="element-139" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                  fontSize: 10,
                  fill: '#9ca3af'
                }} data-id="element-140" />
                    <YAxis domain={[3.5, 5]} hide data-id="element-141" />
                    <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }} data-id="element-142" />
                    <Area type="monotone" dataKey="score" stroke="#1C69D4" strokeWidth={2} fillOpacity={1} fill="url(#colorScore)" data-id="element-143" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Sentiment Analysis */}
            <div data-id="element-144">
              <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-145">
                Kundenstimmung
              </h3>
              <div className="flex flex-wrap gap-2" data-id="element-146">
                <Badge variant="success" className="px-3 py-1 text-sm" data-id="element-147">
                  Beratung +14%
                </Badge>
                <Badge variant="success" className="px-3 py-1 text-sm" data-id="element-148">
                  Fahrzeugqualität +9%
                </Badge>
                <Badge variant="danger" className="px-3 py-1 text-sm" data-id="element-149">
                  Wartezeit -5%
                </Badge>
                <Badge variant="warning" className="px-3 py-1 text-sm" data-id="element-150">
                  Werkstatt -3%
                </Badge>
              </div>
              <p className="text-xs text-gray-500 mt-3 leading-relaxed" data-id="element-151">
                Kunden loben häufig die{' '}
                <span className="font-medium text-gray-900" data-id="element-152">
                  Beratungsqualität
                </span>
                , aber aktuelle Bewertungen erwähnen längere Wartezeiten bei
                Werkstattterminen und der Fahrzeugübergabe.
              </p>
            </div>

            {/* Recent Review Snippet */}
            <div className="p-4 rounded-lg border" style={{
          backgroundColor: '#EEF4FC',
          borderColor: '#C5D9F5'
        }} data-id="element-153">
              <div className="flex items-center justify-between mb-2" data-id="element-154">
                <div className="flex items-center gap-2" data-id="element-155">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{
                backgroundColor: '#1C69D4'
              }} data-id="element-156">
                    M
                  </div>
                  <span className="text-sm font-medium text-gray-900" data-id="element-157">
                    Michael S.
                  </span>
                </div>
                <span className="text-xs text-gray-500" data-id="element-158">vor 2 Tagen</span>
              </div>
              <p className="text-sm text-gray-700 italic" data-id="element-159">
                "Tolle Beratung beim BMW iX, aber die Wartezeit bei der
                Fahrzeugübergabe war leider etwas lang..."
              </p>
              <div className="mt-3 flex justify-end" data-id="element-160">
                <Button size="sm" variant="secondary" className="text-xs h-7" data-id="element-161">
                  Mit KI antworten
                </Button>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="p-4 border-t border-gray-200 bg-gray-50" data-id="element-162">
            <Button className="w-full" data-id="element-163">Vollständigen Bericht anzeigen</Button>
          </div>
        </motion.div>}
    </AnimatePresence>;
}
function MapPinIcon({
  className
}: {
  className?: string;
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-164">
      <path d="M20 10c0 6-9 13-9 13s-9-7-9-13a9 9 0 0 1 18 0z" data-id="element-165" />
      <circle cx="12" cy="10" r="3" data-id="element-166" />
    </svg>;
}
function PhoneIcon({
  className
}: {
  className?: string;
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-167">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.06 6.06l1.27-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" data-id="element-168" />
    </svg>;
}
function MailIcon({
  className
}: {
  className?: string;
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-169">
      <rect width="20" height="16" x="2" y="4" rx="2" data-id="element-170" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" data-id="element-171" />
    </svg>;
}
function GlobeIcon({
  className
}: {
  className?: string;
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-172">
      <circle cx="12" cy="12" r="10" data-id="element-173" />
      <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" data-id="element-174" />
      <path d="M2 12h20" data-id="element-175" />
    </svg>;
}